/**
 * @file themeApi.js
 * @description Lee y escribe el tema que el superadmin fija para toda la plataforma.
 *
 * ⚠ TEMPORAL, junto con el resto de `src/theme/`.
 *
 * El valor del servidor se cachea en `localStorage`. No es una optimización: sin
 * caché, en el primer pintado no sabemos todavía si el superadmin ocultó el
 * selector, y el usuario vería aparecer un control que desaparece medio segundo
 * después. Con la caché, la recarga arranca ya con la última verdad conocida.
 */
import api from '../services/api';
import { DEFAULT_PALETTE, DEFAULT_MODE, isValidPalette, isValidMode } from './palettes';

export const GLOBAL_THEME_CACHE_KEY = 'evazard_theme_global';

export const GLOBAL_FALLBACK = {
    palette: DEFAULT_PALETTE,
    mode: DEFAULT_MODE,
    allow_user_override: true,
};

/** Descarta cualquier cosa rara que venga del servidor o de la caché. */
export const normalizeGlobalTheme = (raw) => ({
    palette: isValidPalette(raw?.palette) ? raw.palette : GLOBAL_FALLBACK.palette,
    mode: isValidMode(raw?.mode) ? raw.mode : GLOBAL_FALLBACK.mode,
    allow_user_override: raw?.allow_user_override !== false,
});

export const readCachedGlobalTheme = () => {
    try {
        const raw = window.localStorage.getItem(GLOBAL_THEME_CACHE_KEY);
        return raw ? normalizeGlobalTheme(JSON.parse(raw)) : null;
    } catch {
        return null;
    }
};

export const writeCachedGlobalTheme = (value) => {
    try {
        window.localStorage.setItem(GLOBAL_THEME_CACHE_KEY, JSON.stringify(value));
    } catch {
        /* Modo privado: se vuelve a pedir al servidor en cada carga. */
    }
};

/**
 * Lectura pública: funciona sin sesión, porque también hay que pintar la landing,
 * el login y el catálogo público.
 * @returns {Promise<object|null>} null si no se pudo consultar (sin red, servidor
 *          caído): quien llama se queda con lo que ya tenía en vez de repintar.
 */
export const fetchGlobalTheme = async () => {
    try {
        const { data } = await api.get('/public/theme');
        const normalizado = normalizeGlobalTheme(data);
        writeCachedGlobalTheme(normalizado);
        return normalizado;
    } catch {
        return null;
    }
};

/** Backoffice: incluye los valores admitidos para no listarlos en el cliente. */
export const fetchAdminThemeSettings = async () => {
    const { data } = await api.get('/admin/theme-settings');
    return data;
};

export const saveAdminThemeSettings = async (patch) => {
    const { data } = await api.put('/admin/theme-settings', patch);
    writeCachedGlobalTheme(normalizeGlobalTheme(data));
    return data;
};
